import { useEffect, useRef, useState } from "react";
import { Card, MonoMeta, StateWord, Label } from "@/design/primitives";
import { StageTrack, PhaseBar } from "@/screens/shared/PhaseTrack";
import { cn } from "@/lib/utils";
import { openInDocs } from "@/lib/docLinkify";

// ── ProjectTile (Desk v3, Jeff 10/29: single-column accordion expand) ──────
// Clicking a tile expands it IN PLACE below the tile header. Only one open at
// a time (managed by parent). Expanded content: right-now strip, phases with
// progress bars, model chips, budget, current tasks, and plan text.
// Motion: edge-glow pulse on active tiles, filling progress bars, live dot.
// All animation respects prefers-reduced-motion.
// Jeff 2026-11-03 (desk restructure): an expanded tile carries a "Park" button
// — it postpones the row to On hold (goal row → TABLED + bay twin → TABLED).

const fmtMoney = (n) =>
  n == null ? "—" : n >= 1 ? `$${Number(n).toFixed(2)}` : `$${Number(n).toFixed(4)}`;

const ParkControl = ({ slug, board, projectsBoard, dispatch, onFlash }) => {
  const [busy, setBusy] = useState(false);
  if (slug === "bay-placeholder") return null;
  // A dispatched project row lives on the PROJECTS board (Jeff 11-04 bug: the
  // park button always posted to goals, so bay tiles born from idea-dispatch
  // silently did nothing). Pick the board the row actually sits on.
  const host = dispatch ? projectsBoard || board : board;
  return (
    <button
      type="button"
      data-testid={`tile-park-${slug}`}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        const res = await host.edit("postpone", slug, { note: "Jeff parked it — on hold." });
        setBusy(false);
        if (res && onFlash) onFlash("Parked — it's on hold now. Bring it back any time.");
        else if (onFlash) onFlash("Couldn't park it — the row didn't answer. Refresh and try again.");
      }}
      className="self-start rounded-control border border-os-border px-2.5 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
      title="Move this to On hold — work stops, nothing is lost"
    >
      {busy ? "parking…" : "park — on hold"}
    </button>
  );
};

// Archive (Jeff 11-04): moves any card clean out of the Desk — no bay, no
// queue, no On hold. archiveAny is sticky through Kit's sync, so it stays out.
const ArchiveControl = ({ slug, board, projectsBoard, dispatch, onFlash }) => {
  const [busy, setBusy] = useState(false);
  if (slug === "bay-placeholder") return null;
  const host = dispatch ? projectsBoard || board : board;
  return (
    <button
      type="button"
      data-testid={`tile-archive-${slug}`}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        const res = await host.act("archiveAny", slug, "Jeff archived it from the Desk — done with it here.");
        setBusy(false);
        if (res && onFlash) onFlash("Archived — out of the desk. It stays archived when Kit syncs.");
        else if (onFlash) onFlash("Couldn't archive it — the row didn't answer. Refresh and try again.");
      }}
      className="self-start rounded-control border border-os-border px-2.5 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
      title="Move this out of the Desk entirely"
    >
      {busy ? "archiving…" : "archive"}
    </button>
  );
};

// Reference files (Jeff 11-04): every project surface shows the docs its plan
// pulls from — chat-born threads carry them in payload.refs/chatShape.refs.
const RefsList = ({ refs }) => {
  const files = (refs || []).filter((f) => f && f.path);
  if (files.length === 0) return null;
  return (
    <div data-testid="tile-refs" className="mt-3 border-t border-os-border pt-3">
      <span className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">Files referenced</span>
      <ul className="mt-1 flex max-h-32 flex-col gap-0.5 overflow-y-auto pr-1">
        {files.slice(0, 8).map((f) => (
          <li key={f.path}>
            <button
              type="button"
              onClick={() => openInDocs(f.path)}
              className="flex w-full items-center gap-1 truncate text-left text-[0.8125rem] text-os-ink-2 underline-offset-2 hover:text-os-ink hover:underline"
            >
              <span className="mr-1 font-mono text-[0.6875rem] text-os-ink-4">{f.name?.endsWith(".md") ? "md" : "pdf"}</span>
              {f.name || f.path.split("/").pop()}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TileRunnerLine = ({ slug }) => {
  const [runner, setRunner] = useState(null);
  const [down, setDown] = useState(false);
  useEffect(() => {
    // Blank-slate placeholder (Jeff 10/30): no live runner exists for it —
    // render the honest "waiting" block inline instead of fetching.
    if (slug === "bay-placeholder") {
      setRunner({
        phases: [
          { title: "Phase 0 — Waiting", status: "todo", isCurrent: true },
          { title: "Task #1 — fills in when Jeff drops a real project", status: "todo" },
        ],
        current: 0,
      });
      return;
    }
    let alive = true;
    fetch(`/api/project/${encodeURIComponent(slug)}/runner`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body) => alive && setRunner(body))
      .catch(() => alive && setDown(true));
    return () => {
      alive = false;
    };
  }, [slug]);
  if (down) return null;
  if (!runner) return <MonoMeta as="p">reading the live feed…</MonoMeta>;
  const cur = (runner.phases || []).find((p) => p.isCurrent) || null;
  if (cur && cur.status !== "done" && !/^\(/.test(String(cur.title || ""))) {
    /* normal current phase */
  } else {
    const queued = (runner.phases || []).find((p) => p.status !== "done" && !/^\(/.test(String(p.title || "")));
    const fallback = (runner.phases || []).find((p) => /^\(/.test(String(p.title || "")));
    const honest = queued
      ? { label: "next up", text: queued.title }
      : fallback
        ? { label: "running", text: String(fallback.title).replace(/^\((.*—.*)\)$/, "$1").trim() || "straight-line — work runs without phase stops" }
        : null;
    if (!honest) return null;
    return (
      <p
        data-testid={`tile-now-${slug}`}
        className="text-[0.8125rem] leading-[1.45] text-os-ink-2"
      >
        <span className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-amber">
          {honest.label}
        </span>{" "}
        {honest.text}
        {runner.lanesOnThisProject?.length ? (
          <span className="text-os-ink-3">
            {" · "}
            {runner.lanesOnThisProject.length} agent lane
            {runner.lanesOnThisProject.length === 1 ? "" : "s"} on it
          </span>
        ) : null}
      </p>
    );
  }
  return (
    <p
      data-testid={`tile-now-${slug}`}
      className="text-[0.8125rem] leading-[1.45] text-os-ink-2"
    >
      <span className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-amber">
        right now
      </span>{" "}
      {cur.title}
      {runner.lanesOnThisProject?.length ? (
        <span className="text-os-ink-3">
          {" · "}
          {runner.lanesOnThisProject.length} agent lane
          {runner.lanesOnThisProject.length === 1 ? "" : "s"} on it
        </span>
      ) : null}
    </p>
  );
};

const ModelChip = ({ primary, fallback }) => {
  if (!primary) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className={cn(
          "whitespace-nowrap rounded-chip border px-1.5 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em]",
          "border-os-border-strong text-os-ink"
        )}
      >
        {primary}
      </span>
      {fallback && (
        <span className="font-mono text-[0.5625rem] text-os-ink-3">
          fallback: {fallback}
        </span>
      )}
    </div>
  );
};

const PhaseList = ({ phases, currentPhase }) => {
  const total = phases.length;
  return (
    <ol className="mt-2 flex flex-col gap-2">
      {phases.map((ph, i) => {
        const done = ph.status === "done";
        const isCurrent = ph.isCurrent;
        const pct = isCurrent ? (ph.progress != null ? ph.progress : 50) : done ? 100 : 0;
        return (
          <li
            key={ph.n || i}
            data-testid={`tile-phase-${ph.n || i + 1}`}
            className="flex flex-col gap-1"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span
                className={cn(
                  "text-[0.8125rem] leading-[1.45]",
                  done ? "text-os-ink-3" : isCurrent ? "font-medium text-os-ink" : "text-os-ink-2"
                )}
              >
                {ph.n != null ? `${ph.n}. ` : ""}{ph.title}
              </span>
              <span
                className={cn(
                  "shrink-0 font-mono text-[0.5625rem] uppercase tracking-[0.08em]",
                  done ? "text-os-green" : isCurrent ? "text-os-amber" : "text-os-ink-3"
                )}
              >
                {done ? "done" : isCurrent ? "running" : "queued"}
              </span>
            </div>
            {isCurrent && (
              <div className="h-1 w-full overflow-hidden rounded-full bg-os-border">
                <div
                  className={cn(
                    "h-full rounded-full bg-os-amber transition-all duration-700 ease-out motion-reduce:transition-none",
                    isCurrent && "motion-safe:animate-fill-progress"
                  )}
                  style={{ width: `${pct}%` }}
                  data-testid={`tile-phase-progress-${ph.n || i + 1}`}
                />
              </div>
            )}
            <ModelChip primary={ph.modelPrimary} fallback={ph.modelFallback} />
          </li>
        );
      })}
    </ol>
  );
};

const TASK_STATE_DISPLAY = {
  ready: { word: "ready", tone: "text-os-amber" },
  running: { word: "running", tone: "text-os-amber" },
  queued: { word: "queued", tone: "text-os-ink-3" },
  awaiting_review: { word: "awaiting review", tone: "text-os-ink-2" },
  done: { word: "done", tone: "text-os-green" },
  failed: { word: "failed", tone: "text-os-red" },
};

// Task row for the left column — Jeff 10/29: with each task he wants (a) which
// phase it's connected to, (b) which model is on it, (c) what it's accomplishing,
// (d) where it sits in the process. Phase comes from the task payload when the
// dispatcher tagged it; otherwise it's riding the project's current phase.
const TaskRow = ({ task, i, slug, defaultPhase }) => {
  const phaseTitle = task.payload?.phase || task.phase || defaultPhase;
  const model = task.payload?.model || task.payload?.executor || null;
  const purpose = task.payload?.prompt || task.payload?.goal || null;
  const disp = TASK_STATE_DISPLAY[task.state] || TASK_STATE_DISPLAY.queued;
  return (
    <li
      key={task.id || i}
      data-testid={`tile-task-${slug}-${i}`}
      className="rounded-chip border border-os-border bg-white px-2.5 py-2"
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-full",
            task.state === "running"
              ? "bg-os-amber motion-safe:animate-pulse"
              : task.state === "done"
                ? "bg-os-green"
                : task.state === "failed"
                  ? "bg-os-red"
                  : "border border-os-border-strong bg-transparent",
          )}
        />
        <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-os-ink">{task.title}</span>
        <span className={cn("shrink-0 font-mono text-[0.5625rem] uppercase tracking-[0.08em]", disp.tone)}>
          {disp.word}
        </span>
      </div>
      <p className="mt-1 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-3">
        {phaseTitle ? `phase: ${phaseTitle}` : "phase: —"}
        {model ? ` · model: ${model}` : ""}
      </p>
      {purpose && (
        <p className="mt-0.5 line-clamp-2 text-[0.75rem] leading-[1.4] text-os-ink-2">
          {String(purpose).slice(0, 140)}
        </p>
      )}
    </li>
  );
};

const ExpandedDetail = ({ card, board, projectsBoard, onFlash }) => {
  const [runner, setRunner] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch(`/api/project/${encodeURIComponent(card.slug)}/runner`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body) => {
        if (alive) {
          setRunner(body);
          setFailed(false);
        }
      })
      .catch(() => alive && setFailed(true));
    // Left column feed (Jeff 10/29): real tasks for THIS project, newest first.
    // Auth failures (401) read as "no tasks" — quiet, not an error state.
    fetch(`/api/exec/tasks?project=${encodeURIComponent(card.slug)}`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body) => alive && setTasks((body && body.items) || []))
      .catch(() => alive && setTasks([]));
    return () => { alive = false; };
  }, [card.slug]);

  const curPhase = runner?.phases?.find((p) => p.isCurrent);
  const budget = runner?.budget;
  const recentTasks = (tasks || []).slice(0, 5);

  return (
    <div
      data-testid={`tile-expanded-${card.slug}`}
      onClick={(e) => e.stopPropagation()}
      className="overflow-hidden border-t border-os-border-strong bg-os-wash px-5 pb-5 pt-4 motion-safe:transition-all motion-safe:duration-300 shadow-[inset_0_2px_6px_rgba(0,0,0,0.05)]"
    >
      {/* Right-now strip */}
      <div className="flex flex-col gap-2">
        {curPhase && (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-amber">
              right now
            </span>
            <span className="text-[0.875rem] font-medium text-os-ink">{curPhase.title}</span>
            <ModelChip primary={curPhase.modelPrimary} fallback={curPhase.modelFallback} />
          </div>
        )}
        {budget && (
          <p data-testid={`tile-budget-${card.slug}`} className="text-[0.8125rem] text-os-ink-2">
            Budget: {fmtMoney(budget.weeklySpent)} of {fmtMoney(budget.weeklyCap)} this week
            {budget.weeklyCap && budget.weeklySpent != null
              ? ` · ${Math.round((100 * budget.weeklySpent) / budget.weeklyCap)}%`
              : ""}
          </p>
        )}
        {runner?.lanesOnThisProject?.length > 0 && (
          <p className="text-[0.8125rem] text-os-ink-2">
            {runner.lanesOnThisProject.length} agent lane{runner.lanesOnThisProject.length === 1 ? "" : "s"} on it
          </p>
        )}
      </div>

      {/* Two-column detail (Jeff 10/29): tasks LEFT, phases RIGHT. */}
      <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* LEFT: current tasks, up to 5, scrollable in place */}
        <div data-testid={`tile-tasks-col-${card.slug}`} className="flex min-w-0 flex-col">
          <Label as="h4" className="!text-[0.6875rem]">Current tasks</Label>
          {recentTasks.length > 0 ? (
            <ul
              data-testid={`tile-tasklist-${card.slug}`}
              className="mt-1.5 flex max-h-56 flex-col gap-1.5 overflow-y-auto pr-1"
            >
              {recentTasks.map((t, i) => (
                <TaskRow
                  key={t.id || i}
                  task={t}
                  i={i}
                  slug={card.slug}
                  defaultPhase={curPhase?.title}
                />
              ))}
            </ul>
          ) : (
            <p
              data-testid={`tile-tasks-none-${card.slug}`}
              className="mt-2 text-[0.8125rem] text-os-ink-3"
            >
              {tasks === null ? "reading tasks…" : "No queued tasks on this project right now."}
            </p>
          )}
        </div>

        {/* RIGHT: phases with color-coded state */}
        <div data-testid={`tile-phases-col-${card.slug}`} className="flex min-w-0 flex-col">
          <Label as="h4" className="!text-[0.6875rem]">Phases</Label>
          {runner?.phases?.length > 0 ? (
            <PhaseList phases={runner.phases} currentPhase={runner.current} />
          ) : (
            <p className="mt-2 text-[0.8125rem] text-os-ink-3">
              {failed ? "Live feed unavailable." : "No phases recorded yet."}
            </p>
          )}
        </div>
      </div>

      {/* Plan text (collapsed at bottom) */}
      {(card.goal || card.why) && (
        <div className="mt-4 border-t border-os-border pt-3">
          <p className="text-[0.8125rem] leading-[1.5] text-os-ink-3">
            <span className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">Goal · </span>
            {card.goal || card.why}
          </p>
        </div>
      )}

      {/* Reference files (Jeff 11-04): open straight into the docs reader */}
      <RefsList refs={card.refs || card.chatShape?.refs || []} />

      {failed && (
        <p className="mt-2 text-[0.8125rem] text-os-ink-3">
          Live feed unavailable — phases above are from the last saved state.
        </p>
      )}

      {/* Desk restructure (Jeff 2026-11-03): park from the tile — the row
          moves to On hold; the bay twin parks with it. Jeff 11-04: archive
          joins park — dispatched rows act on their real board. */}
      <div className="mt-3 flex items-center gap-2 border-t border-os-border pt-3">
        <ParkControl slug={card.slug} board={board} projectsBoard={projectsBoard} dispatch={card.dispatch} onFlash={onFlash} />
        <ArchiveControl slug={card.slug} board={board} projectsBoard={projectsBoard} dispatch={card.dispatch} onFlash={onFlash} />
      </div>
    </div>
  );
};

export const ProjectTile = ({ card, expanded, onToggleExpand, board, projectsBoard, onFlash }) => {
  const p = card.detail ? card : { ...card, detail: card.detail || {} };
  const live = card.rawState === "EXECUTE";
  const sig = `${card.stateWord}|${card.next}|${card.checksVerified}/${card.checksTotal}`;
  const prevSig = useRef(sig);
  const [changed, setChanged] = useState(false);
  useEffect(() => {
    if (prevSig.current !== sig) {
      prevSig.current = sig;
      setChanged(true);
      const t = setTimeout(() => setChanged(false), 1000);
      return () => clearTimeout(t);
    }
  }, [sig]);

  return (
    <Card
      role="button"
      tabIndex={0}
      data-testid={`desk-tile-${card.slug}`}
      data-state={card.rawState}
      data-changed={changed ? "true" : undefined}
      aria-label={`${card.title} — ${expanded ? "collapse" : "expand"} details`}
      onClick={onToggleExpand}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onToggleExpand?.())}
      className={cn(
        "flex cursor-pointer flex-col text-left transition-shadow duration-300 motion-reduce:transition-none",
        live && "motion-safe:shadow-[0_0_0_0_rgba(0,0,0,0)] motion-safe:hover:shadow-[0_0_12px_2px_rgba(var(--os-green)/0.25)]",
        changed && "desk-flash-once",
      )}
    >
      <div className="p-5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink">
            {card.title}
          </h3>
          <StateWord tone={card.stateTone || (live ? "green" : "amber")}>
            {card.stateWord || (live ? "Being built" : "Taking shape")}
          </StateWord>
        </div>
        <p className="mt-0.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
          {card.brand || ""}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <StageTrack state={card.rawState} className="min-w-[8rem] flex-[2] basis-32" />
          <PhaseBar payload={card} />
        </div>
        <p
          data-testid={`tile-next-${card.slug}`}
          className="mt-3 flex-1 text-[0.875rem] font-medium leading-[1.45] text-os-ink"
        >
          {(card.next || "").replace(/^Next: /, "")}
        </p>
        {live && <TileRunnerLine slug={card.slug} />}
        <div className="mt-3 border-t border-os-border pt-3">
          <MonoMeta as="p" data-testid={`tile-checks-${card.slug}`}>
            {(Number(card.checksTotal) || 0) > 0
              ? `${card.checksVerified || 0} of ${card.checksTotal} checks verified`
              : `${card.phaseCount || 0} planned steps`}
          </MonoMeta>
        </div>
      </div>
      {expanded && <ExpandedDetail card={card} board={board} projectsBoard={projectsBoard} onFlash={onFlash} />}
    </Card>
  );
};

export default ProjectTile;
